import { forwardRef, useState } from 'react';
import type { FormEvent, ChangeEvent } from 'react';
import { Send } from 'lucide-react';
import { SectionAtmosphere } from '../layout/HoldingBackdrop';

const Footer = forwardRef<HTMLElement>((_, ref) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const subject = encodeURIComponent(`Website enquiry — ${formData.name}`);
    const body = encodeURIComponent(
      `Name: ${formData.name}\nEmail: ${formData.email}\n\nMessage:\n${formData.message}`
    );
    window.location.href = `mailto:info@fidusfinance.com?subject=${subject}&body=${body}`;
    setSubmitted(true);
    setFormData({ name: '', email: '', message: '' });
    window.setTimeout(() => setSubmitted(false), 5000);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <footer ref={ref} id="contact" className="relative isolate overflow-hidden border-t border-[#e8ede4]/10">
      <SectionAtmosphere variant="contact" />
      <div className="relative z-10 mx-auto max-w-7xl px-5 py-16 text-[#d6ddcf]/85 md:px-8 md:py-20">
        <div className="mb-14 grid gap-14 md:grid-cols-2">
          <div className="order-2 md:order-1">
            <h3 className="mb-6 text-3xl font-semibold tracking-[-0.04em] text-[#f1f5ec]/90">Contact us</h3>
            {submitted ? (
              <p className="mb-4 rounded-2xl border border-teal-200/25 bg-black/25 px-4 py-3 text-sm leading-relaxed text-[#e8ede4]/78">
                Your email client should open with the message pre-filled. If it does not, write to{' '}
                <a
                  href="mailto:info@fidusfinance.com"
                  className="text-teal-200/90 underline decoration-teal-200/30 underline-offset-4 hover:decoration-teal-200/70"
                >
                  info@fidusfinance.com
                </a>
                .
              </p>
            ) : null}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="name" className="mb-2 block text-[0.65rem] font-semibold uppercase tracking-[0.28em] text-[#d6ddcf]/85">
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-[#e8ede4]/10 bg-black/25 px-4 py-3 text-[#f1f5ec] transition placeholder:text-[#d6ddcf]/80 focus:border-teal-200/40 focus:outline-none focus:ring-1 focus:ring-teal-200/25"
                  required
                  autoComplete="name"
                />
              </div>
              <div>
                <label htmlFor="email" className="mb-2 block text-[0.65rem] font-semibold uppercase tracking-[0.28em] text-[#d6ddcf]/85">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-[#e8ede4]/10 bg-black/25 px-4 py-3 text-[#f1f5ec] transition focus:border-teal-200/40 focus:outline-none focus:ring-1 focus:ring-teal-200/25"
                  required
                  autoComplete="email"
                  pattern="[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}"
                />
              </div>
              <div>
                <label htmlFor="message" className="mb-2 block text-[0.65rem] font-semibold uppercase tracking-[0.28em] text-[#d6ddcf]/85">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className="w-full resize-none rounded-xl border border-[#e8ede4]/10 bg-black/25 px-4 py-3 text-[#f1f5ec] transition focus:border-teal-200/40 focus:outline-none focus:ring-1 focus:ring-teal-200/25"
                  required
                />
              </div>
              <button
                type="submit"
                className="flex items-center justify-center gap-2 rounded-full bg-[#e8ede4] px-6 py-3 text-sm font-semibold text-[#07101d] shadow-lg shadow-black/25 transition hover:bg-teal-100"
              >
                Send message <Send className="h-4 w-4" aria-hidden />
              </button>
            </form>
          </div>

          <div className="order-1 md:order-2">
            <div className="grid gap-10">
              <div>
                <h3 className="mb-4 text-2xl font-semibold tracking-[-0.03em] text-[#f1f5ec]/90">Fidus Finance</h3>
                <p className="mb-8 text-sm leading-7">
                  Brazilian technology company headquartered in Florianópolis. We build software for
                  energy infrastructure — plant telemetry, dispatch and operational data.
                </p>
                <div className="space-y-6 text-sm leading-relaxed">
                  <div>
                    <p className="mb-1 font-medium text-[#f1f5ec]">Brazil</p>
                    <p className="text-[#d6ddcf]/85">
                      Rod. José Carlos Daux, 5500
                      <br />
                      Sala 401, Florianópolis
                      <br />
                      88032-000, Brazil
                    </p>
                  </div>
                </div>
              </div>
              <div>
                <h4 className="mb-4 text-[0.65rem] font-semibold uppercase tracking-[0.28em] text-[#d6ddcf]/85">
                  Quick links
                </h4>
                <ul className="space-y-2 text-sm">
                  <li>
                    <a href="#about" className="transition hover:text-teal-100">
                      About
                    </a>
                  </li>
                  <li>
                    <a href="#services" className="transition hover:text-teal-100">
                      Services
                    </a>
                  </li>
                  <li>
                    <a href="#office" className="transition hover:text-teal-100">
                      Office
                    </a>
                  </li>
                  <li>
                    <a href="#security" className="transition hover:text-teal-100">
                      Security
                    </a>
                  </li>
                  <li>
                    <a href="#contact" className="transition hover:text-teal-100">
                      Contact
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-[#e8ede4]/10 pt-10 text-center text-sm text-[#d6ddcf]/85">
          <p>© {new Date().getFullYear()} Fidus Finance. All rights reserved.</p>
          <a
            href="mailto:info@fidusfinance.com"
            className="mt-2 inline-block text-teal-200/85 transition hover:text-teal-100"
          >
            info@fidusfinance.com
          </a>
        </div>
      </div>
    </footer>
  );
});

Footer.displayName = 'Footer';
export default Footer;
